<template>
  <div class="ledger-page">
    <b-card
      v-if="canRun"
      class="rounded-lg"
    >
      <h4 class="mb-1">
        {{ $t('ledger_depr_title') }}
      </h4>
      <b-row>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_year')">
            <b-form-select
              v-model.number="year"
              :options="yearOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_month')">
            <b-form-select
              v-model.number="month"
              :options="monthOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="d-flex align-items-end mb-1"
        >
          <b-button
            variant="outline-primary"
            class="rounded-pill"
            block
            :disabled="previewing"
            @click="loadPreview"
          >
            {{ $t('ledger_depr_preview') }}
          </b-button>
        </b-col>
      </b-row>

      <b-alert
        v-if="preview && preview.already_posted"
        show
        variant="info"
      >
        {{ $t('ledger_depr_already') }}
        <b-link
          v-if="preview.run_id"
          :to="{ name: 'ledger-depreciation-view', params: { id: preview.run_id } }"
        >
          {{ $t('ledger_depr_open_run') }}
        </b-link>
      </b-alert>

      <template v-if="preview && !preview.already_posted">
        <div class="d-flex justify-content-between mb-1">
          <strong>{{ periodLabel(preview) }}</strong>
          <span class="ledger-num">{{ money(preview.total_amount) }}</span>
        </div>
        <div
          v-if="!(preview.lines || []).length"
          class="text-muted mb-1"
        >
          {{ $t('ledger_depr_nothing') }}
        </div>
        <div
          v-else
          class="d-none d-md-block"
        >
          <b-table
            :items="preview.lines"
            :fields="previewFields"
            small
            responsive
            class="ledger-dense-table"
          >
            <template #cell(asset)="data">
              {{ assetLabel(data.item.asset) }}
            </template>
            <template #cell(amount)="data">
              <span class="ledger-num">{{ money(data.item.amount) }}</span>
            </template>
            <template #cell(book_value_after)="data">
              <span class="ledger-num">{{ money(data.item.book_value_after) }}</span>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="(line, index) in preview.lines"
            :key="index"
            class="ledger-asset-card"
          >
            <div>{{ assetLabel(line.asset) }}</div>
            <div class="ledger-num">
              {{ money(line.amount) }}
            </div>
          </article>
        </div>
        <div
          v-if="(preview.journal || []).length"
          class="mt-1"
        >
          <h6>{{ $t('ledger_depr_journal') }}</h6>
          <div
            v-for="(line, index) in preview.journal"
            :key="index"
            class="d-flex justify-content-between ledger-num"
          >
            <span>{{ line.code }}</span>
            <span>{{ $t('ledger_debit') }} {{ money(line.debit) }} · {{ $t('ledger_credit') }} {{ money(line.credit) }}</span>
          </div>
        </div>
        <b-button
          v-if="(preview.lines || []).length"
          variant="primary"
          class="rounded-pill mt-1"
          :disabled="running"
          @click="confirmRun"
        >
          {{ $t('ledger_depr_run') }}
        </b-button>
      </template>

      <b-alert
        v-if="showLinksHint"
        show
        variant="warning"
        class="mt-1 mb-0"
      >
        <b-link :to="{ name: 'ledger-links' }">
          {{ $t('ledger_links_title') }}
        </b-link>
      </b-alert>
    </b-card>

    <b-card class="rounded-lg">
      <h5 class="mb-1">
        {{ $t('ledger_depr_runs') }}
      </h5>
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!runs.length"
        class="text-muted"
      >
        {{ $t('ledger_depr_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="runs"
            :fields="runFields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(period)="data">
              <b-link :to="{ name: 'ledger-depreciation-view', params: { id: data.item.id } }">
                {{ periodLabel(data.item) }}
              </b-link>
            </template>
            <template #cell(total_amount)="data">
              <span class="ledger-num">{{ money(data.item.total_amount) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusBadgeVariant(enumValue(data.item.status))">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
            </template>
            <template #cell(journal)="data">
              <b-link
                v-if="journalId(data.item) && canOpenJournal"
                :to="journalRoute(journalId(data.item))"
              >
                {{ $t('ledger_journal_open') }}
              </b-link>
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="canRun && enumValue(data.item.status) === 'posted'"
                size="sm"
                variant="outline-danger"
                @click="confirmReverse(data.item)"
              >
                {{ $t('ledger_depr_reverse') }}
              </b-button>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="run in runs"
            :key="run.id"
            class="ledger-asset-card"
          >
            <div class="d-flex justify-content-between">
              <b-link :to="{ name: 'ledger-depreciation-view', params: { id: run.id } }">
                {{ periodLabel(run) }}
              </b-link>
              <b-badge :variant="statusBadgeVariant(enumValue(run.status))">
                {{ enumLabel(run.status, $i18n.locale) }}
              </b-badge>
            </div>
            <div class="ledger-num">
              {{ money(run.total_amount) }} · {{ run.asset_count }}
            </div>
            <b-button
              v-if="canRun && enumValue(run.status) === 'posted'"
              size="sm"
              variant="outline-danger"
              class="mt-50"
              @click="confirmReverse(run)"
            >
              {{ $t('ledger_depr_reverse') }}
            </b-button>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BAlert, BLink, BTable, BBadge, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getDepreciationRuns, previewDepreciation, createDepreciationRun, reverseDepreciationRun,
} from '@/api/accounting/fixedAssets'
import {
  listFrom, oneFrom, enumValue, enumLabel, formatLedgerMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

function previousMonth() {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 1)
  return { year: date.getFullYear(), month: date.getMonth() + 1 }
}

export default {
  name: 'DepreciationRuns',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BAlert, BLink, BTable, BBadge, BSpinner,
  },
  data() {
    const previous = previousMonth()
    return {
      year: previous.year,
      month: previous.month,
      preview: null,
      previewing: false,
      running: false,
      loading: false,
      runs: [],
      showLinksHint: false,
    }
  },
  computed: {
    canRun() {
      return this.hasPermission('run depreciation')
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    now() {
      const date = new Date()
      return { year: date.getFullYear(), month: date.getMonth() + 1 }
    },
    yearOptions() {
      const years = []
      for (let year = this.now.year; year >= this.now.year - 6; year -= 1) years.push(year)
      return years
    },
    monthOptions() {
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1
        const future = this.year > this.now.year || (this.year === this.now.year && month > this.now.month)
        return {
          value: month,
          text: new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, index, 1)),
          disabled: future,
        }
      })
    },
    previewFields() {
      return [
        { key: 'asset', label: this.$t('ledger_assets_register') },
        { key: 'amount', label: this.$t('ledger_assets_amount') },
        { key: 'book_value_after', label: this.$t('ledger_assets_nbv') },
      ]
    },
    runFields() {
      const fields = [
        { key: 'period', label: this.$t('ledger_payroll_period') },
        { key: 'total_amount', label: this.$t('ledger_assets_amount') },
        { key: 'asset_count', label: this.$t('ledger_depr_count') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'journal', label: this.$t('ledger_journals_title') },
      ]
      if (this.canRun) fields.push({ key: 'actions', label: this.$t('ledger_actions') })
      return fields
    },
  },
  watch: {
    year() {
      this.clampMonth()
      this.preview = null
    },
    month() {
      this.preview = null
    },
  },
  mounted() {
    this.loadRuns()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    periodLabel(row) {
      if (!row) return ''
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const month = new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, row.month - 1, 1))
      return `${month} ${row.year}`
    },
    assetLabel(asset) {
      if (!asset) return ''
      const name = this.$i18n.locale === 'en' ? (asset.name || asset.name_ar) : (asset.name_ar || asset.name)
      return asset.asset_code ? `${asset.asset_code} — ${name || ''}` : (name || '')
    },
    journalId(run) {
      return (run && (run.journal_entry_id || (run.journal_entry && run.journal_entry.id))) || null
    },
    journalRoute(id) {
      return { name: 'ledger-journals-view', params: { id } }
    },
    clampMonth() {
      if (this.year === this.now.year && this.month > this.now.month) this.month = this.now.month
    },
    toast(title, variant) {
      const icons = { success: 'CheckIcon', warning: 'AlertTriangleIcon', info: 'InfoIcon', danger: 'AlertCircleIcon' }
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: icons[variant] || 'InfoIcon', variant },
      })
    },
    async loadRuns() {
      this.loading = true
      try {
        const res = await getDepreciationRuns()
        this.runs = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadPreview() {
      if (!this.canRun) return
      this.previewing = true
      this.showLinksHint = false
      try {
        const res = await previewDepreciation({ year: this.year, month: this.month })
        this.preview = oneFrom(res)
      } catch (error) {
        this.preview = null
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.previewing = false
      }
    },
    async confirmRun() {
      if (!this.canRun || this.running) return
      const result = await Swal.fire({
        text: this.$t('ledger_depr_run_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_depr_run'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.running = true
      try {
        const res = await createDepreciationRun({ year: this.year, month: this.month })
        const status = res && res.status
        const run = oneFrom(res && res.data)
        if (status === 200) {
          this.toast(this.$t('ledger_depr_already_toast'), 'info')
        } else {
          this.toast(this.$t('ledger_depr_posted'), 'success')
        }
        const journalId = this.journalId(run)
        if (status !== 200 && journalId && this.canOpenJournal) {
          this.$router.push(this.journalRoute(journalId))
          return
        }
        if (run && run.id) {
          this.$router.push({ name: 'ledger-depreciation-view', params: { id: run.id } })
          return
        }
        await this.loadRuns()
        await this.loadPreview()
      } catch (error) {
        const message = apiErrorMessage(error, this.$t('ledger_error'))
        this.toast(message, error?.response?.status === 422 ? 'warning' : 'danger')
        const text = String(message || '').toLowerCase()
        this.showLinksHint = (text.includes('ربط') || text.includes('unmapped') || text.includes('link'))
          && this.hasPermission('edit gl_links')
      } finally {
        this.running = false
      }
    },
    async confirmReverse(run) {
      if (!this.canRun) return
      const result = await Swal.fire({
        text: this.$t('ledger_depr_reverse_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_depr_reverse'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await reverseDepreciationRun(run.id)
        this.toast(this.$t('ledger_depr_reversed'), 'success')
        this.preview = null
        await this.loadRuns()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-asset-card {
  border-bottom: 1px solid #eee;
  padding: 0.75rem 0.15rem;
}
</style>
